/*
  Styled dropdowns for the collection / search toolbar (sort and per-page).

  Progressive enhancement over the real <select>: the select stays in the page
  (visually hidden) and remains the source of truth, so every existing handler
  keeps working - choosing an option sets select.value and dispatches the
  native `change` event that the section script, collection-ajax.js and
  search-sort.js already listen for. Without JavaScript the plain select shows.

  Markup is built from the select's own options, so the search page's extra
  browser-side sorts appear automatically.
*/
(function () {
  'use strict';

  var CHECK = '<svg class="collection-dropdown__check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
  var CHEVRON = '<svg class="collection-dropdown__chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>';
  var uid = 0;

  function enhance(select) {
    if (select.dataset.dropdownReady === 'true') return;
    select.dataset.dropdownReady = 'true';
    uid += 1;

    var label = select.id ? document.querySelector('label[for="' + select.id + '"]') : null;
    var name = label ? label.textContent.trim() : '';

    var wrap = document.createElement('div');
    wrap.className = 'collection-dropdown';
    select.parentNode.insertBefore(wrap, select);
    wrap.appendChild(select);
    select.classList.add('collection-dropdown__native');
    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'collection-dropdown__button';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    var listId = 'CollectionDropdown-' + uid;
    button.setAttribute('aria-controls', listId);
    button.innerHTML = '<span class="collection-dropdown__value"></span>' + CHEVRON;
    wrap.appendChild(button);

    var list = document.createElement('ul');
    list.className = 'collection-dropdown__list';
    list.id = listId;
    list.setAttribute('role', 'listbox');
    list.tabIndex = -1;
    list.hidden = true;
    if (name) list.setAttribute('aria-label', name);
    wrap.appendChild(list);

    var valueNode = button.querySelector('.collection-dropdown__value');
    var active = -1;

    var items = function () { return Array.prototype.slice.call(list.children); };

    var render = function () {
      list.innerHTML = '';
      Array.prototype.forEach.call(select.options, function (option, index) {
        if (option.hidden) return;
        var item = document.createElement('li');
        item.className = 'collection-dropdown__option';
        item.id = listId + '-' + index;
        item.setAttribute('role', 'option');
        item.dataset.value = option.value;
        item.innerHTML = '<span></span>' + CHECK;
        item.firstChild.textContent = option.textContent.trim();
        list.appendChild(item);
      });
      sync();
    };

    var sync = function () {
      var selected = select.options[select.selectedIndex];
      valueNode.textContent = selected ? selected.textContent.trim() : '';
      button.setAttribute('aria-label', (name ? name + ': ' : '') + valueNode.textContent);
      items().forEach(function (item) {
        var isSelected = item.dataset.value === select.value;
        item.setAttribute('aria-selected', String(isSelected));
        item.classList.toggle('is-selected', isSelected);
      });
    };

    var setActive = function (index) {
      var all = items();
      if (!all.length) return;
      active = (index + all.length) % all.length;
      all.forEach(function (item, i) { item.classList.toggle('is-active', i === active); });
      list.setAttribute('aria-activedescendant', all[active].id);
      all[active].scrollIntoView({ block: 'nearest' });
    };

    var open = function () {
      if (!list.hidden) return;
      closeOthers(wrap);
      sync();
      list.hidden = false;
      wrap.classList.add('is-open');
      button.setAttribute('aria-expanded', 'true');
      // Open to the left when the panel would run past the right edge.
      wrap.classList.remove('collection-dropdown--align-end');
      if (list.getBoundingClientRect().right > document.documentElement.clientWidth - 8) {
        wrap.classList.add('collection-dropdown--align-end');
      }
      var selectedIndex = items().findIndex(function (item) { return item.dataset.value === select.value; });
      setActive(selectedIndex < 0 ? 0 : selectedIndex);
      list.focus();
    };

    var close = function (focusButton) {
      if (list.hidden) return;
      list.hidden = true;
      wrap.classList.remove('is-open');
      button.setAttribute('aria-expanded', 'false');
      list.removeAttribute('aria-activedescendant');
      if (focusButton) button.focus();
    };
    wrap.closeDropdown = close;

    var choose = function (value) {
      close(true);
      if (value === select.value) return;
      select.value = value;
      sync();
      select.dispatchEvent(new Event('change', { bubbles: true }));
    };

    button.addEventListener('click', function () {
      if (list.hidden) open(); else close(true);
    });
    button.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        open();
      }
    });
    list.addEventListener('click', function (event) {
      var item = event.target.closest('.collection-dropdown__option');
      if (item) choose(item.dataset.value);
    });
    list.addEventListener('mousemove', function (event) {
      var item = event.target.closest('.collection-dropdown__option');
      if (item) setActive(items().indexOf(item));
    });
    list.addEventListener('keydown', function (event) {
      var all = items();
      if (event.key === 'ArrowDown') { event.preventDefault(); setActive(active + 1); }
      else if (event.key === 'ArrowUp') { event.preventDefault(); setActive(active - 1); }
      else if (event.key === 'Home') { event.preventDefault(); setActive(0); }
      else if (event.key === 'End') { event.preventDefault(); setActive(all.length - 1); }
      else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (all[active]) choose(all[active].dataset.value);
      } else if (event.key === 'Escape') { event.preventDefault(); close(true); }
      else if (event.key === 'Tab') { close(false); }
    });

    // The section script restores ?sort_by= into the select without a change
    // event, and other scripts may too; keep the label in step either way.
    select.addEventListener('change', sync);
    window.addEventListener('pageshow', sync);
    window.addEventListener('load', sync);
    document.addEventListener('collection:results-updated', sync);

    render();
    // Catch a value restored by a script that runs after this one.
    setTimeout(sync, 0);
  }

  function closeOthers(except) {
    document.querySelectorAll('.collection-dropdown.is-open').forEach(function (wrap) {
      if (wrap !== except && wrap.closeDropdown) wrap.closeDropdown(false);
    });
  }

  document.addEventListener('click', function (event) {
    if (!event.target.closest('.collection-dropdown')) closeOthers(null);
  });

  function init(scope) {
    (scope || document).querySelectorAll('.collection-controls__select').forEach(enhance);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
